import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { X } from '@/utils/icons';
import type { DayData, TerraActivityItem } from './DayCell';
import { PROVIDER_LOGOS } from './DayCell';

interface DayDetailProps {
    dateStr: string;
    data: DayData | null;
    onClose: () => void;
}

const ProviderImg: React.FC<{ provider: keyof typeof PROVIDER_LOGOS; size?: number }> = ({ provider, size = 18 }) => {
    const { isDark } = useTheme();
    const logo = PROVIDER_LOGOS[provider];
    const src = 'src' in logo ? logo.src : (isDark ? logo.dark : logo.light);
    return <img src={src} alt={logo.alt} className="flex-shrink-0 object-contain" style={{ width: size, height: size }} />;
};

const Section: React.FC<{
    title: string;
    provider: keyof typeof PROVIDER_LOGOS;
    children: React.ReactNode;
}> = ({ title, provider, children }) => {
    const { isDark } = useTheme();
    return (
        <div className={`rounded-lg p-4 ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.02]'}`}>
            <div className="flex items-center gap-2 mb-3">
                <ProviderImg provider={provider} size={20} />
                <h4 className="text-sm font-semibold cgm-text-primary">{title}</h4>
            </div>
            {children}
        </div>
    );
};

const Stat: React.FC<{ label: string; value: string | number | null | undefined }> = ({ label, value }) => {
    if (value == null || value === '') return null;
    return (
        <div className="flex justify-between py-1">
            <span className="text-xs cgm-text-secondary">{label}</span>
            <span className="text-xs font-medium cgm-text-primary">{value}</span>
        </div>
    );
};

function formatDate(dateStr: string): string {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

function formatMinutes(mins: number | null | undefined): string {
    if (mins == null) return '-';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function fmtSec(secs: number | null | undefined): string {
    if (secs == null) return '-';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function fmtPace(secsPerKm: number | null | undefined): string {
    if (secsPerKm == null) return '-';
    const m = Math.floor(secsPerKm / 60);
    const s = Math.round(secsPerKm % 60);
    return `${m}:${s < 10 ? '0' : ''}${s} /km`;
}

const DayDetail: React.FC<DayDetailProps> = ({ dateStr, data, onClose }) => {
    const { isDark } = useTheme();
    const hasSomething = data && (data.garmin || data.sleep || data.catapult?.length || data.hawkin?.length || data.terra_activities?.length);

    const panelBg = isDark ? '#0d0d0d' : '#ffffff';

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />

            {/* Panel */}
            <div
                className="fixed top-0 right-0 h-full w-full max-w-md z-50 shadow-2xl overflow-y-auto animate-slide-in-right"
                style={{ backgroundColor: panelBg }}
            >
                {/* Sticky header with fully opaque background */}
                <div
                    className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b"
                    style={{
                        backgroundColor: panelBg,
                        borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                    }}
                >
                    <h3 className="text-base font-semibold cgm-text-primary">{formatDate(dateStr)}</h3>
                    <button onClick={onClose} className="cgm-text-secondary hover:cgm-text-primary transition-colors cursor-pointer">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {!hasSomething && (
                        <p className="text-sm cgm-text-secondary text-center py-12">No data recorded for this day.</p>
                    )}

                    {data?.garmin && (
                        <Section title="Daily Activity" provider="garmin">
                            <Stat label="Steps" value={data.garmin.steps?.toLocaleString()} />
                            <Stat label="Distance" value={data.garmin.distance_m ? `${(data.garmin.distance_m / 1000).toFixed(1)} km` : null} />
                            <Stat label="Calories" value={data.garmin.calories ? Math.round(data.garmin.calories) : null} />
                            <Stat label="Avg HR" value={data.garmin.hr_avg ? `${Math.round(data.garmin.hr_avg)} bpm` : null} />
                            <Stat label="Resting HR" value={data.garmin.resting_hr ? `${Math.round(data.garmin.resting_hr)} bpm` : null} />
                        </Section>
                    )}

                    {data?.terra_activities?.length ? (
                        <Section title="Activities" provider="terra_activity">
                            {data.terra_activities.map((act: TerraActivityItem, i: number) => (
                                <div key={act.terra_activity_id || i} className={`${i > 0 ? 'mt-3 pt-3 border-t' : ''}`}
                                    style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
                                    <p className="text-xs font-medium cgm-text-primary mb-1">{act.display_label}</p>
                                    <Stat label="Duration" value={fmtSec(act.duration_s)} />
                                    <Stat label="Distance" value={act.distance_m ? `${(act.distance_m / 1000).toFixed(1)} km` : null} />
                                    <Stat label="Calories" value={act.calories ? Math.round(act.calories) : null} />
                                    <Stat label="Avg HR" value={act.hr_avg ? `${Math.round(act.hr_avg)} bpm` : null} />
                                    <Stat label="Max HR" value={act.hr_max ? `${Math.round(act.hr_max)} bpm` : null} />
                                    <Stat label="Pace" value={act.pace_s_per_km ? fmtPace(act.pace_s_per_km) : null} />
                                    <Stat label="Elev Gain" value={act.elevation_gain_m ? `${Math.round(act.elevation_gain_m)} m` : null} />
                                </div>
                            ))}
                        </Section>
                    ) : null}

                    {data?.sleep && (
                        <Section title="Sleep" provider="sleep">
                            <Stat label="Duration" value={formatMinutes(data.sleep.total_minutes)} />
                            <Stat label="Deep" value={formatMinutes(data.sleep.deep_minutes)} />
                            <Stat label="REM" value={formatMinutes(data.sleep.rem_minutes)} />
                            <Stat label="Light" value={formatMinutes(data.sleep.light_minutes)} />
                            <Stat label="Efficiency" value={data.sleep.efficiency != null ? `${data.sleep.efficiency}%` : null} />
                            <Stat label="Quality Score" value={data.sleep.quality_score} />
                        </Section>
                    )}

                    {data?.catapult?.length ? (
                        <Section title="Catapult Sessions" provider="catapult">
                            {data.catapult.map((s, i) => (
                                <div key={s.id || i} className={`${i > 0 ? 'mt-3 pt-3 border-t' : ''}`}
                                    style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
                                    <p className="text-xs font-medium cgm-text-primary mb-1">{s.name || 'Session'}</p>
                                    <Stat label="Duration" value={s.duration_min ? `${s.duration_min} min` : null} />
                                    <Stat label="Distance" value={s.distance_m ? `${(s.distance_m / 1000).toFixed(1)} km` : null} />
                                    <Stat label="Player Load" value={s.player_load} />
                                    <Stat label="Athletes" value={s.athlete_count} />
                                </div>
                            ))}
                        </Section>
                    ) : null}

                    {data?.hawkin?.length ? (
                        <Section title="Hawkin Tests" provider="hawkin">
                            {data.hawkin.map((t, i) => (
                                <div key={t.id || i} className={`${i > 0 ? 'mt-3 pt-3 border-t' : ''}`}
                                    style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
                                    <p className="text-xs font-medium cgm-text-primary mb-1">{t.test_type || 'Test'}</p>
                                    {t.athlete_name && <Stat label="Athlete" value={t.athlete_name} />}
                                    <Stat label="Jump Height" value={t.jump_height_m != null ? `${(t.jump_height_m * 100).toFixed(1)} cm` : null} />
                                    <Stat label="Peak Force" value={t.peak_force_n != null ? `${Math.round(t.peak_force_n)} N` : null} />
                                    <Stat label="RSI" value={t.rsi != null ? t.rsi.toFixed(3) : null} />
                                </div>
                            ))}
                        </Section>
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default DayDetail;
